import { useState } from "react";
import { AlertTriangle, Waves } from "lucide-react";

import { cn } from "@/lib/utils";
import { ManualActionError, type ManualCockpitState } from "../../hooks/useManualCockpit";

/**
 * Mode range du cockpit MANUEL (owner 2026-07-31) : SELL au plafond, BUY au
 * plancher, sur la bande que l'owner pose lui-même. Un bouton, sans OTP —
 * l'automate ne vit qu'à l'intérieur de la fenêtre armée que l'OTP a déjà
 * payée (range-only 2026-09-01 : c'est la SEULE ouverture du cockpit).
 *
 * Ce que la carte doit rendre lisible, parce que ce sont les garde-fous :
 * les deux côtés démarrent DÉSARMÉS (il faut être entré dans la bande avant de
 * pouvoir en sortir), un bord ne tire qu'une fois par touche, et le bord opposé
 * retourne la position au lieu d'en empiler une seconde.
 */

/** Largeur minimale acceptée côté backend (ManualRangeService.MIN_WIDTH_POINTS). */
export const RANGE_MIN_WIDTH_POINTS = 30;

export default function ManualRangeCard({ manual }: { manual: ManualCockpitState }) {
  const [floor, setFloor] = useState("");
  const [ceiling, setCeiling] = useState("");
  const [size, setSize] = useState("1");
  const [stopDist, setStopDist] = useState("120");
  const [confirming, setConfirming] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const armed = manual.status?.armed ?? false;
  const range = manual.status?.range;
  const active = range?.active ?? false;

  const floorNum = Number(floor);
  const ceilingNum = Number(ceiling);
  const sizeNum = Number(size);
  const stopNum = Number(stopDist);
  const width = ceilingNum - floorNum;
  const numbersOk =
    [floorNum, ceilingNum, sizeNum, stopNum].every((n) => Number.isFinite(n) && n > 0) &&
    floor.trim() !== "" &&
    ceiling.trim() !== "";
  const valid = numbersOk && width >= RANGE_MIN_WIDTH_POINTS;
  const hint = !numbersOk
    ? null
    : width <= 0
      ? "Le plafond doit être au-dessus du plancher."
      : width < RANGE_MIN_WIDTH_POINTS
        ? `Bande de ${width.toFixed(0)} pts — minimum ${RANGE_MIN_WIDTH_POINTS} pts.`
        : null;

  const resetForm = () => setConfirming(false);

  const submit = async () => {
    setErr(null);
    if (!confirming) {
      setConfirming(true);
      return;
    }
    try {
      await manual.startRange(floorNum, ceilingNum, sizeNum, stopNum);
      setConfirming(false);
    } catch (e) {
      setConfirming(false);
      setErr(
        e instanceof ManualActionError ? `${e.code} — ${e.detail}` : "Mode range refusé.",
      );
    }
  };

  const stop = async () => {
    setErr(null);
    try {
      await manual.stopRange();
    } catch {
      setErr("Impossible d'arrêter le mode range.");
    }
  };

  if (!armed) {
    return null;
  }

  return (
    <div
      className={cn(
        "flex flex-col gap-3 rounded-xl border p-4 transition-colors",
        active ? "border-info/45 bg-info-soft" : "border-border bg-background/30",
      )}
      data-testid="manual-range-card"
      data-active={active}
    >
      <div className="flex items-center gap-3">
        <div
          className={cn(
            "grid size-9 shrink-0 place-items-center rounded-lg border",
            active ? "border-info/40 bg-info/10 text-info" : "border-border text-muted-foreground",
          )}
        >
          <Waves className="size-5" aria-hidden />
        </div>
        <div className="min-w-0">
          <div className="text-foreground flex items-center gap-2 font-semibold">
            Mode range
            {active && (
              <span className="text-info inline-flex items-center gap-1 text-xs font-medium">
                <span className="bg-info size-1.5 animate-pulse rounded-full" /> actif
              </span>
            )}
          </div>
          <div className="text-muted-foreground text-xs">
            SELL au plafond, BUY au plancher — sur ta bande, sans OTP.
          </div>
        </div>
      </div>

      {active && range ? (
        <div
          className="border-border flex flex-col gap-2.5 rounded-lg border bg-card p-3"
          data-testid="manual-range-state"
        >
          <dl className="grid grid-cols-2 gap-y-1.5 text-sm">
            <dt className="text-muted-foreground">Bande</dt>
            <dd className="text-right font-mono tabular-nums" data-testid="manual-range-band">
              {range.floorLevel} — {range.ceilingLevel}
            </dd>
            <dt className="text-muted-foreground">Taille · stop</dt>
            <dd className="text-right font-mono tabular-nums">
              {range.sizePerPointEur} €/pt · {range.stopDistancePoints} pts
            </dd>
            <dt className="text-muted-foreground">Déclenchements</dt>
            <dd className="text-right font-mono tabular-nums">{range.triggerCount}</dd>
          </dl>
          <div className="flex gap-2 text-xs">
            <SideBadge label="Plafond · SELL" ready={range.sellArmed} />
            <SideBadge label="Plancher · BUY" ready={range.buyArmed} />
          </div>
          {range.lastEvent && (
            <p className="text-muted-foreground text-xs" data-testid="manual-range-last-event">
              {range.lastEvent}
            </p>
          )}
          {err && (
            <p className="text-loss text-sm" role="alert" data-testid="manual-range-error">
              {err}
            </p>
          )}
          <button
            type="button"
            onClick={() => void stop()}
            disabled={manual.pending}
            className="border-border text-foreground hover:border-info rounded-lg border px-4 py-2 text-sm font-bold disabled:opacity-50"
            data-testid="manual-range-stop"
          >
            Arrêter le mode range
          </button>
          <p className="text-muted-foreground text-xs">
            L'arrêt ne clôture rien : les positions ouvertes restent sous leur stop.
          </p>
        </div>
      ) : (
        <div className="border-border flex flex-col gap-3 rounded-lg border bg-card p-3">
          <div className="grid grid-cols-2 gap-2">
            <Field
              label="Plancher (BUY)"
              value={floor}
              onChange={setFloor}
              onEdit={resetForm}
              testid="manual-range-floor"
            />
            <Field
              label="Plafond (SELL)"
              value={ceiling}
              onChange={setCeiling}
              onEdit={resetForm}
              testid="manual-range-ceiling"
            />
            <Field
              label="Taille (€/pt)"
              value={size}
              onChange={setSize}
              onEdit={resetForm}
              step="0.5"
              testid="manual-range-size"
            />
            <Field
              label="Stop (pts, obligatoire)"
              value={stopDist}
              onChange={setStopDist}
              onEdit={resetForm}
              testid="manual-range-stop-dist"
            />
          </div>
          {hint && (
            <p className="text-warning text-xs" data-testid="manual-range-hint">
              {hint}
            </p>
          )}
          {err && (
            <p className="text-loss text-sm" role="alert" data-testid="manual-range-error">
              {err}
            </p>
          )}
          <button
            type="button"
            onClick={() => void submit()}
            disabled={manual.pending || !valid}
            className={cn(
              "rounded-lg px-4 py-2 text-sm font-bold disabled:opacity-50",
              confirming ? "bg-warning text-black" : "bg-info text-black",
            )}
            data-testid="manual-range-submit"
          >
            {manual.pending
              ? "Activation…"
              : confirming
                ? `Confirmer la bande ${floor} — ${ceiling} · ${size} €/pt · stop ${stopDist} pts`
                : "Activer le mode range"}
          </button>
          <p className="text-muted-foreground flex items-start gap-1.5 text-xs">
            <AlertTriangle className="text-warning mt-0.5 size-3.5 shrink-0" aria-hidden />
            Automate : il passe des ordres réels tout seul tant que le cockpit est armé. Les
            deux bords démarrent désarmés — le prix doit d'abord revenir dans la bande. Un bord
            ne tire qu'une fois par touche, et le bord opposé retourne la position au lieu d'en
            empiler une deuxième.
          </p>
        </div>
      )}
    </div>
  );
}

function Field({
  label,
  value,
  onChange,
  onEdit,
  testid,
  step = "1",
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  onEdit: () => void;
  testid: string;
  step?: string;
}) {
  return (
    <label className="text-muted-foreground flex flex-col gap-1 text-xs">
      {label}
      <input
        type="number"
        min="0"
        step={step}
        value={value}
        onChange={(e) => {
          onChange(e.target.value);
          onEdit();
        }}
        className="bg-background border-border focus:border-info text-foreground rounded-lg border px-3 py-2 font-mono text-sm outline-none"
        data-testid={testid}
      />
    </label>
  );
}

/** Un bord est « prêt » quand l'hystérésis l'a réarmé — sinon il a déjà tiré. */
function SideBadge({ label, ready }: { label: string; ready: boolean }) {
  return (
    <span
      className={cn(
        "inline-flex items-center gap-1.5 rounded-md border px-2 py-1",
        ready ? "border-info/40 text-info" : "border-border text-muted-foreground",
      )}
    >
      <span className={cn("size-1.5 rounded-full", ready ? "bg-info" : "bg-muted")} />
      {label} — {ready ? "prêt" : "déjà tiré"}
    </span>
  );
}
